import { convertFlowchartDataToGraph } from './flowchartToGraph';
import {
  generateAttributeClaims,
  generateGeneralSessionManagementProfiles,
  generateRelyingPartiesXML,
  parseConditionalDAG,
  setClaimAutoDefaults,
} from './fqSpecGeneration';
import { BranchVarMap, statJsonFlowchart } from './jsonFlowchartStat';
import {
  addNewUserJourney,
  addSupportedLanguageToLocalization,
  createFQClientDefinition,
  createFQLocalizationString,
  createFQTechnicalProfile,
  generateXMLFromFQSpecs,
  getOrCreateFQContentDefinition,
  initJEParsingContent,
  JE_ParsingContext,
} from './jsonFQContext';
import { extractSettingFieldValue, extractTemplatizableField, getTenantSettings, initSettings, nameToPolicyId, TemplatizationInfo } from './utils';
import settings from '../constants/jsonXMLsettings';
import defaultLocalization from '../constants/defaultLocalization';
import { validateJson } from './jsonValidation/jsonValidation';
import { getRunNowLink } from './policyUpload';
//import { sendChunkedAIDataTrace } from './policyUpload';

const generateTPEDefaultTP = (parsingCtx: JE_ParsingContext) => {
  createFQTechnicalProfile(
    parsingCtx.technicalProfileStore,
    {
      id: 'TpEngine_c3bd4fe2-1775-4013-b91d-35f16d377d13',
      displayName: 'Trustframework Policy Engine Default Technical Profile',
      protocol: { name: 'None' },
      metadata: [{ key: 'url', value: '{service:te}' }],
    },
    'Trustframework Policy Engine TechnicalProfiles',
  );
};

const loadDefaultLocalization = (parsingCtx: JE_ParsingContext) => {
  defaultLocalization.contentDefinitions.forEach((contentDef) => {
    let cdfId = getOrCreateFQContentDefinition(
      parsingCtx,
      {
        id: contentDef.id,
        loadUri: contentDef.loadUri,
        recoveryUri: contentDef.recoveryUri,
        dataUri: contentDef.dataUri,
        metadata: contentDef.metadata.map((mEntry) => {
          return { key: mEntry.key, value: mEntry.value };
        }),
        localizedResourcesReferences: contentDef.localizedResourcesRefs
          ? {
              mergeBehavior: contentDef.localizedResourcesRefs.mergeBehavior,
              references: contentDef.localizedResourcesRefs.localizedResourcesRefs.map((lrr) => {
                return { language: lrr.language, referenceId: lrr.localizedResourcesReferenceId };
              }),
            }
          : undefined,
      },
      contentDef.id,
      true,
    ).id;
    if (!parsingCtx.globalReusables.defaultContentDefinitions) {
      parsingCtx.globalReusables.defaultContentDefinitions = {};
    }
    parsingCtx.globalReusables.defaultContentDefinitions[contentDef.id] = cdfId;
  });
  addSupportedLanguageToLocalization(parsingCtx, defaultLocalization.supportedLanguages.defaultLanguage);
  defaultLocalization.localizedResources.forEach((lr) => {
    lr.strings.forEach((lstring) => {
      createFQLocalizationString(
        parsingCtx,
        {
          elementType: lstring.elementType,
          elementId: lstring.elementId,
          stringId: lstring.stringId,
          value: lstring.value,
        },
        lr.id,
      );
    });
  });
};

export interface RelyingPartyReference {
  xml: string;
  policyName: string;
  isSamlPolicy: boolean;
  samlIdPMetadataUrls?: {[tpName: string]: string};
}

export const generatePolicyXML = (
  jsonBase: { [id: string]: any },
  sessionId: string,
  otherSettingsJson?: { [id: string]: any },
  templatizationSettings?: any
) => {
  if (!sessionId) {
    sessionId =
      'session-' +
      Math.floor(Math.random() * 10000000).toString() +
      '-' +
      Math.floor(Math.random() * 10000000).toString(); // TODO: Have a more central and robust session ID
  }
  let logId = '';
  if (settings.enableAIJsonAndXmlLog) {
    logId = Math.floor(Math.random() * 1000).toString();
    //sendChunkedAIDataTrace(jsonString, { sessionId: sessionId, itemType: 'json' });
  }
  // Get the build settings.
  let buildSettings = initSettings(otherSettingsJson, templatizationSettings);
  // Get tenant metadata and settings information
  let tenantId = extractSettingFieldValue('Tenant', buildSettings);
  let policyName = buildSettings.xmlGenerationSettings.basePolicyId;
  if (buildSettings.xmlGenerationSettings.addRandomPolicyIdTail) {
    policyName += Math.floor(Math.random() * 100000).toString();
  }
  // Stage 1: Convert flowchart from editor JSON
  let { flowchartIndices, nodes } = convertFlowchartDataToGraph(jsonBase.flowcharts, jsonBase.nodeData, buildSettings);
  let parsingCtx = initJEParsingContent(buildSettings);
  generateTPEDefaultTP(parsingCtx);
  if (settings.includeDefaultLocalization) {
    loadDefaultLocalization(parsingCtx);
  }
  createFQClientDefinition(parsingCtx.clientDefinitionStore, {
    id: 'DefaultWeb',
    uiFilterFlags: 'LineMarkers, MetaRefresh',
  });
  generateAttributeClaims(parsingCtx, jsonBase.claims);
  generateGeneralSessionManagementProfiles(parsingCtx, nodes, jsonBase.ssoSession);
  // Identify non-subflow charts and create userJourneys for them to store subflow branch vars into
  let userInfoEndpoints = Object.keys(flowchartIndices).filter((flowchartId: string) => {
    return flowchartIndices[flowchartId].type === 'userInfoEndpoint';
  });
  userInfoEndpoints.forEach((endpointJourneyId: string) => {
    addNewUserJourney(parsingCtx.userJourneyStore, endpointJourneyId, 'endpoint');
  });
  let journeys = Object.keys(flowchartIndices).filter((flowchartId: string) => {
    return flowchartIndices[flowchartId].type === 'UserJourney';
  });
  journeys.forEach((journeyId: string) => {
    addNewUserJourney(
      parsingCtx.userJourneyStore,
      journeyId,
      'userJourney',
      buildSettings.xmlGenerationSettings.enableJavascript,
      nameToPolicyId(flowchartIndices[journeyId].name),
    );
  });
  // Stage 2: Stat flowchart structures.
  // Start with subflows.
  let subflowIds = Object.keys(flowchartIndices).filter((flowchartId: string) => {
    return !['userInfoEndpoint', 'UserJourney'].includes(flowchartIndices[flowchartId].type);
  });
  let subflowTopologicalOrders: { [flowId: string]: string[] } = {};
  let subflowBranchVarMaps: { [flowId: string]: BranchVarMap } = {};
  subflowIds.forEach((subflowId: string) => {
    let { topologicalOrder, branchVarMap } = statJsonFlowchart(
      parsingCtx,
      flowchartIndices[subflowId].root,
      nodes,
      flowchartIndices[subflowId].parentFlow!,
      flowchartIndices,
    );
    subflowTopologicalOrders[subflowId] = topologicalOrder;
    subflowBranchVarMaps[subflowId] = branchVarMap;
  });
  // Handle userInfoEndpoints, both stage 2 (structure stat) and Stage 3 (FQspec gen)
  userInfoEndpoints.forEach((endpointJourneyId: string) => {
    let { topologicalOrder, branchVarMap } = statJsonFlowchart(
      parsingCtx,
      flowchartIndices[endpointJourneyId].root,
      nodes,
      endpointJourneyId,
      flowchartIndices,
    );
    parseConditionalDAG(
      parsingCtx,
      endpointJourneyId,
      nodes,
      topologicalOrder,
      branchVarMap,
      subflowTopologicalOrders,
      subflowBranchVarMaps,
    );
  });
  // Handle userJourneys, both stage 2 (structure stat) and Stage 3 (FQspec gen)
  journeys.forEach((journeyId: string) => {
    let { topologicalOrder, branchVarMap } = statJsonFlowchart(
      parsingCtx,
      flowchartIndices[journeyId].root,
      nodes,
      journeyId,
      flowchartIndices,
    );
    parseConditionalDAG(
      parsingCtx,
      journeyId,
      nodes,
      topologicalOrder,
      branchVarMap,
      subflowTopologicalOrders,
      subflowBranchVarMaps,
    );
  });
  setClaimAutoDefaults(parsingCtx);
  if (settings.outputExtraBranchVarInfo) {
    const branchVarInfo: { [index: string]: any } = {};
    Object.keys(parsingCtx.branchVarMetadata).forEach((branchVar) => {
      branchVarInfo[branchVar] = {
        ancestors: Array.from(parsingCtx.branchVarMetadata[branchVar].ancestors),
        directChildren: Array.from(parsingCtx.branchVarMetadata[branchVar].directChildren),
        descendants: Array.from(parsingCtx.branchVarMetadata[branchVar].descendants),
        mutex: Array.from(parsingCtx.branchVarMetadata[branchVar].mutex),
        setInProfiles: Array.from(parsingCtx.branchVarMetadata[branchVar].setInProfiles)
      };
    });
    console.log(JSON.stringify({ branchVariableMetadata: branchVarInfo }));
  }
  // Stage 4 (Convert FQSpec -> XML)
  let policyXML = generateXMLFromFQSpecs(parsingCtx, tenantId, policyName);
  let relyingParties = generateRelyingPartiesXML(parsingCtx, tenantId, policyName);
  /*if (settings.enableAIJsonAndXmlLog) {
    sendChunkedAIDataTrace(policyXML, { sessionId: sessionId, itemType: 'xmlMain' });
    relyingParties.forEach((rpFile, index) => {
      sendChunkedAIDataTrace(rpFile[0], { sessionId: sessionId, itemType: 'xmlRPFile', rpFileIndex: index.toString() });
    });
  }*/
  let policyNames = relyingParties.map((rp) => rp.policyName);
  policyNames.push(policyName);
  return {
    policyXML: policyXML,
    relyingParties: relyingParties,
    testNowUris: relyingParties.map((rp) => {
      return getRunNowLink(rp.policyName, buildSettings, rp.isSamlPolicy);
    }),
    allPolicyNames: policyNames,
    buildSettings: buildSettings
  };
};
